"use client";
import "@styles/admission.css"
import "./download.css";
import { RiDownloadCloudLine } from "react-icons/ri";
import Link from "next/link";
import { useState, useEffect, useRef, RefObject } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { logError } from "../shared";

import {
  FacebookIconDark,
  InstagramIconDark,
  TwitterIconDark,
} from "@icons/svgs";

import { downloadData } from "./downloadData";

const facebookURL = "http://facebook.com/theoicnm/";
const instagramURL = "https://www.instagram.com/theoicnm/";
const twitterURL = "https://twitter.com/theoicnm";

type FormData = {
  email: string;
  file: string;
};

export function DownloadComponent() {
  const [tab, setTab] = useState<string>("table");
  const [fileName, setFileName] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const formRef = useRef(null);

  const form = useForm<FormData>();
  const { register, handleSubmit, reset, formState } = form;
  const { errors } = formState;

  const scrollToForm = (ref: RefObject<HTMLElement>) => {
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleClick = (fileName: string) => {
    setTab("form");
    setFileName(fileName);
    // scrollToForm(formRef)
  };
  useEffect(() => {
    if (tab === "form") {
      console.log("scrolling to form");
      scrollToForm(formRef);
    }
  }, [tab]);

  const submitForm: SubmitHandler<FormData> = async (data) => {
    const newData = { ...data, file: fileName };
    setButtonDisabled(true);
    try {
      const response = await fetch("/api/downloads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newData),
      });
      const resData = await response.json() as string;
      const statusCode = response.status;
      if (statusCode === 200) {
        reset();
        setTab("success");
      } else if (statusCode === 429) {
        reset();
        setTab("limit");
      } else if (statusCode === 400) {
        setMessage("Bad Request");
        setTimeout(() => {
          setMessage("");
          setButtonDisabled(false);
          setTab("table");
        }, 3000);
      } else {
        setMessage("Internal Server Error");
        setTimeout(() => {
          setMessage("");
          setButtonDisabled(false);
          setTab("table");
        }, 3000);
      }
      return resData;
    } catch (error: any) {
      logError(error, data.email)
      reset();
      setTab("limit");
    }
  };


  return (
    <section>
      {tab === "table" && (
        <section className="fees-table">
          <table>
            <thead>
              <tr>
                <th scope="col">Download</th>
                <th scope="col" colSpan={2}>File Name</th>
                <th scope="col">File Size</th>
                <th scope="col">File Type</th>
              </tr>
            </thead>
            <tbody>
              {downloadData.map((download) => {
                return (
                  <tr key={download.file_name}>
                    {download.direct_download ? (
                      <td className="download-btn">
                        <Link
                          rel="noopener noreferrer"
                          locale={false}
                          download
                          target="_blank"
                          href={`${download.link}`}
                          style={{ textDecoration: "none" }}
                          aria-label={`Download ${download.file_name} (${download.file_type}, ${download.file_size})`}
                        >
                          <RiDownloadCloudLine
                            className="download-cloud"
                            aria-hidden="true"
                          />
                        </Link>
                      </td>
                    ) : (
                      <td className="download-btn">
                        <button
                          type="button"
                          aria-label={`Request ${download.file_name} by email`}
                          onClick={() => handleClick(`${download.file_name}`)}
                          className="bg-transparent border-0 p-0 cursor-pointer"
                        >
                          <RiDownloadCloudLine
                            className="download-cloud"
                            aria-hidden="true"
                          />
                        </button>
                      </td>
                    )}
                    <td colSpan={2}>{download.file_name}</td>
                    <td>{download.file_size}</td>
                    <td>{download.file_type}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}
      {tab === "form" && (
        <section
          ref={formRef}
          style={{ scrollMarginTop: "110px" }}
          className="form-section"
        >
          <h3>Enter your email address</h3>
          <p>
            To download {fileName}, please enter your email address below and
            click on submit.
          </p>
          <p>We will send the download link to your email.</p>
         
          <form onSubmit={handleSubmit(submitForm)}>
            <div className="form-group">
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                {...register("email", {
                  required: "Please enter email!",
                  pattern: {
                    value:
                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: "Please enter a valid email!",
                  },
                  validate: {
                    notThisEmail: (fieldValue) => {
                      return (
                        fieldValue != "admin@example.com" ||
                        "Please enter a different email"
                      );
                    },
                    notBadEmail: (fieldValue) => {
                      return (
                        !fieldValue.endsWith("@example.com") ||
                        "Please enter a different email"
                      );
                    },
                  },
                })}
                type="email"
                autoComplete="email"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "errors" : undefined}
                id="email"
                placeholder="Email"
              />
              {errors.email && (
                <p id="errors" role="alert">
                  {errors.email.message}
                </p>
              )}
              <p id="message" role="status">
                {message}
              </p>
              <button disabled={buttonDisabled}>Submit</button>
            </div>
          </form>
        </section>
      )}
      {tab === "success" && (
        <section
          ref={formRef}
          style={{ scrollMarginTop: "110px" }}
          className="success-section"
        >
          <div className="success">
            <p>Hurray!</p>
            <p>
              You&#39;re awesome. {fileName} has been sent to the email you
              indicated.
            </p>
            <p style={{ marginTop: "30px", marginBottom: "30px" }}>
              Stay in touch by connecting with us on social media!
            </p>
          </div>
          <div className="socials">
            <div className="icon-box">
              <Link
                href={facebookURL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="OICNM on Facebook (opens in a new tab)"
              >
                <FacebookIconDark className="dark-icon" />
              </Link>
            </div>
            <div className="icon-box">
              <Link
                href={instagramURL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="OICNM on Instagram (opens in a new tab)"
              >
                <InstagramIconDark className="dark-icon" />
              </Link>
            </div>
            <div className="icon-box">
              <Link
                href={twitterURL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="OICNM on X (Twitter) (opens in a new tab)"
              >
                <TwitterIconDark className="dark-icon" />
              </Link>
            </div>
          </div>
        </section>
      )}
      {tab === "limit" && (
        <section
          ref={formRef}
          style={{ scrollMarginTop: "110px" }}
          className="limit-section"
        >
          <div className="limit">
            <p>
              You&apos;ve reached the maximum number of requests allowed within
              this hour. To ensure a smooth experience for everyone, please try
              again in one hour.
            </p>
            <p style={{ marginTop: "30px", marginBottom: "30px" }}>
              Stay in touch by connecting with us on social media!
            </p>
          </div>
          <div className="socials">
            <div className="icon-box">
              <Link
                href={facebookURL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="OICNM on Facebook (opens in a new tab)"
              >
                <FacebookIconDark className="dark-icon" />
              </Link>
            </div>
            <div className="icon-box">
              <Link
                href={instagramURL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="OICNM on Instagram (opens in a new tab)"
              >
                <InstagramIconDark className="dark-icon" />
              </Link>
            </div>
            <div className="icon-box">
              <Link
                href={twitterURL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="OICNM on X (Twitter) (opens in a new tab)"
              >
                <TwitterIconDark className="dark-icon" />
              </Link>
            </div>
          </div>
        </section>
      )}
    </section>
  );
}
